<template>
    <v-card v-if="items.length > 0" class="upload-panel" elevation="8">
        <div class="upload-head">
            <v-icon small :color="counts.failed > 0 && counts.active === 0 ? 'error' : 'primary'" class="mr-2">
                {{ counts.active > 0 ? 'mdi-upload' : counts.failed > 0 ? 'mdi-alert-circle' : 'mdi-check-circle' }}
            </v-icon>
            <div class="upload-title">{{ title }}</div>
            <v-btn icon x-small @click="expanded = !expanded">
                <v-icon small>{{ expanded ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
            </v-btn>
            <v-btn icon x-small class="ml-1" :disabled="counts.active > 0" @click="onClose">
                <v-icon small>mdi-close</v-icon>
            </v-btn>
        </div>

        <v-progress-linear
            :value     = "overall"
            :color     = "counts.failed > 0 ? 'warning' : 'primary'"
            height     = "4"
        />

        <div v-if="expanded" class="upload-body">
            <div v-for="item in uploading" :key="item.key" class="upload-item">
                <div class="upload-name">{{ item.file.name }}</div>
                <div class="upload-where">{{ item.siteLabel }} / {{ item.folderLabel }}</div>
                <v-progress-linear :value="item.progress * 100" height="6" color="primary" rounded />
            </div>

            <div v-if="counts.active > 0" class="upload-summary">
                <span v-if="waiting > 0">{{ $t('img.upload.waiting', { n: waiting }) }}</span>
                <span v-if="processing > 0">{{ $t('img.upload.processing', { n: processing }) }}</span>
            </div>

            <div v-for="item in failed" :key="item.key" class="upload-item">
                <div class="upload-name error--text">{{ item.file.name }}</div>
                <div class="upload-where">
                    {{ item.siteLabel }} / {{ item.folderLabel }} · {{ reason(item) }}
                    <a v-if="retryable(item)" class="ml-1" @click="Uploads.retry(item)">{{ $t('img.upload.retry') }}</a>
                </div>
            </div>
        </div>
    </v-card>
</template>

<script>
import Core from "@/modules/core/service/Core";
import Uploads from "@/modules/img/components/service/Uploads";
import { FAILED, IN_PROGRESS, UPLOADING, WAITING } from "@/modules/img/components/service/UploadQueue";

export default {
    name: "ImgUploadPanel",

    data: () => ({
        expanded: true,
        Uploads,
        stopFinished: null,
    }),

    computed: {
        items () {
            return Uploads.state.items;
        },

        counts () {
            return Uploads.counts();
        },

        uploading () {
            return this.items.filter(item => item.state === UPLOADING);
        },

        failed () {
            return this.items.filter(item => item.state === FAILED);
        },

        waiting () {
            return this.items.filter(item => item.state === WAITING).length;
        },

        processing () {
            return this.items.filter(item => item.state === IN_PROGRESS).length;
        },

        overall () {
            const { total, done, failed } = this.counts;
            return total === 0 ? 0 : ((done + failed) * 100) / total;
        },

        title () {
            const { total, done, failed, active } = this.counts;
            if (active > 0) {
                return this.$t("img.upload.running", { done: done + failed, total });
            }
            return failed > 0
                ? this.$t("img.upload.finishedFailed", { done, failed })
                : this.$t("img.upload.finished", { done });
        }
    },

    mounted () {
        this.stopFinished = Uploads.onFinished(({ done, failed }) => {
            Core.message.hint(this, failed > 0
                ? this.$t("img.upload.finishedFailed", { done, failed })
                : this.$t("img.upload.finished", { done }));
        });
        window.addEventListener("beforeunload", this.onBeforeUnload);
    },

    beforeDestroy () {
        if (this.stopFinished) {
            this.stopFinished();
        }
        window.removeEventListener("beforeunload", this.onBeforeUnload);
    },

    methods: {
        // Closing the tab would cut off what is still waiting or running
        onBeforeUnload (event) {
            if (Uploads.counts().active > 0) {
                event.preventDefault();
                event.returnValue = "";
            }
        },

        onClose () {
            Uploads.clear();
        },

        retryable (item) {
            return item.reason !== "TOO_LARGE" && item.reason !== "UNSUPPORTED";
        },

        reason (item) {
            if (!item.reason) {
                return this.$t("img.upload.state.FAILED");
            }
            const key = "img.upload.reason." + item.reason;
            return this.$te(key) ? this.$t(key) : item.reason;
        }
    }
}
</script>

<style scoped>
.upload-panel {
    position: fixed;
    right: 16px;
    bottom: 16px;
    width: 360px;
    max-width: calc(100vw - 32px);
    z-index: 300;
}

.upload-head {
    display: flex;
    align-items: center;
    padding: 8px 10px 6px 12px;
}

.upload-title {
    flex: 1;
    font-size: 14px;
    font-weight: 500;
}

.upload-body {
    max-height: 260px;
    overflow-y: auto;
    padding: 4px 12px 10px;
}

.upload-item {
    margin-top: 8px;
    font-size: 13px;
}

.upload-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.upload-where, .upload-summary {
    font-size: 12px;
    color: gray;
    margin-bottom: 2px;
}

.upload-summary span + span {
    margin-left: 10px;
}
</style>
